import { useState } from "react";
import { Link } from "react-router";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import {
  useUserInformationLookup,
  useUserInformationSearch,
  UserInformationResponse,
  UserSearchHit,
} from "src/dm/hooks/useUserInformation";

const DOMAINS = ["GiantEagle", "Daymon"] as const;

const UserInformationPage = () => {
  const [domain, setDomain] = useState<string>("GiantEagle");
  const [userId, setUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [result, setResult] = useState<UserInformationResponse | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const lookup = useUserInformationLookup();
  const search = useUserInformationSearch(searchQuery, domain, searchEnabled);

  const runLookup = async (id: string) => {
    setLookupError(null);
    try {
      const data = await lookup.mutateAsync({ userId: id, domain });
      setResult(data);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      const status = axiosErr.response?.status;
      const apiMessage = axiosErr.response?.data?.message;
      if (status === 404) {
        setResult(null);
        setLookupError(apiMessage ?? `User '${id}' was not found.`);
        return;
      }
      if (status === 403) {
        setLookupError(apiMessage ?? "Admin access required.");
        return;
      }
      setLookupError(apiMessage ?? axiosErr.message ?? "Lookup failed.");
    }
  };

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Link
        to="/dm/admin"
        style={{
          fontSize: "0.85rem",
          color: "#0a3d62",
          textDecoration: "none",
        }}
      >
        ← Admin
      </Link>
      <h1 style={{ margin: 0 }}>Show User Information</h1>
      <p style={{ color: "#475569", marginTop: "-0.5rem" }}>
        Look up a user's DM privileges and per-subject scope (cat-mgr, vendor,
        banner). Pick a domain and either type a user id directly or search
        the roster below.
      </p>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          display: "grid",
          gap: "0.75rem",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "160px 1fr auto",
            gap: "0.5rem",
            alignItems: "end",
          }}
        >
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Domain
            </span>
            <DropDownList
              data={[...DOMAINS]}
              value={domain}
              onChange={(e) => setDomain(String(e.value ?? "GiantEagle"))}
            />
          </label>
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              User id (e.g. approver1)
            </span>
            <Input
              value={userId}
              onChange={(e) => setUserId(String(e.target.value ?? ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && userId.trim()) {
                  runLookup(userId.trim());
                }
              }}
            />
          </label>
          <Button
            themeColor="primary"
            disabled={lookup.isPending || !userId.trim()}
            onClick={() => runLookup(userId.trim())}
          >
            {lookup.isPending ? "Looking up…" : "Get user info"}
          </Button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.5rem",
            alignItems: "end",
          }}
        >
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Search users in domain by id or name
            </span>
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(String(e.target.value ?? ""));
                setSearchEnabled(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSearchEnabled(true);
              }}
            />
          </label>
          <Button onClick={() => setSearchEnabled(true)}>Search</Button>
        </div>

        {search.isFetching && (
          <p style={{ color: "#475569", margin: 0 }}>Searching…</p>
        )}
        {searchEnabled && search.data && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "0.5rem",
            }}
          >
            {search.data.items.length === 0 && (
              <p style={{ color: "#94a3b8", margin: 0 }}>
                No users matched that query.
              </p>
            )}
            {search.data.items.map((hit: UserSearchHit) => (
              <button
                key={hit.userId}
                type="button"
                onClick={() => {
                  setUserId(hit.userId);
                  runLookup(hit.userId);
                }}
                style={{
                  background: "#f1f5f9",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  padding: "0.5rem 0.75rem",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <strong>{hit.userId}</strong>
                <div style={{ fontSize: "0.8rem", color: "#475569" }}>
                  {hit.fullName ?? ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {lookupError && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            padding: "0.75rem",
            color: "#b91c1c",
          }}
        >
          {lookupError}
        </div>
      )}

      {result && (
        <section
          style={{
            background: "#fff",
            padding: "1rem 1.25rem",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>
            {result.userId}
            {result.fullName ? ` — ${result.fullName}` : ""}
          </h2>
          <p style={{ color: "#64748b", margin: "0 0 0.75rem" }}>
            Domain: {result.domain ?? "—"}
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <div>
              <h3 style={{ fontSize: "0.9rem", marginBottom: "0.25rem" }}>
                Privileges
              </h3>
              {result.privileges.length === 0 ? (
                <p style={{ color: "#94a3b8", margin: 0 }}>
                  No DM privileges granted.
                </p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                  {result.privileges.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 style={{ fontSize: "0.9rem", marginBottom: "0.25rem" }}>
                Subject Scope
              </h3>
              {result.subjects.length === 0 ? (
                <p style={{ color: "#94a3b8", margin: 0 }}>
                  No per-subject scope rows.
                </p>
              ) : (
                <dl style={{ margin: 0 }}>
                  {result.subjects.map((g) => (
                    <div
                      key={g.subjectType}
                      style={{ marginBottom: "0.5rem" }}
                    >
                      <dt
                        style={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                        }}
                      >
                        {g.subjectType}
                      </dt>
                      <dd style={{ margin: "0 0 0 0.5rem" }}>
                        {g.subjectValues.join(", ")}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default UserInformationPage;
